import type { OrderDetail } from "../api/types";
import { Panel, formatMoney } from "../components/ui";

/**
 * What was ordered, exactly as it was when it was ordered.
 *
 * EVERY VALUE HERE IS A SNAPSHOT THE BACKEND STORED AT ORDER TIME.
 * Nothing in this panel looks a product up. If the frame has since been
 * renamed, repriced, unpublished or archived, this still shows the name the
 * customer bought and the price they were charged — which is the only version
 * of the order that is true, and the one an operator needs when the customer
 * calls about it six months later.
 *
 * The total is the stored, server-authoritative amount. It is displayed, never
 * recomputed: adding the lines up here would create a second source of truth
 * that could disagree with the money the shop actually charged.
 */
export function OrderLinesPanel({ order }: { order: OrderDetail }) {
  const discounted = order.savings_minor > 0;

  return (
    <Panel
      title={
        <>
          Items <span className="orders__count">{order.lines.length}</span>
        </>
      }
      bodyClass=""
    >
      <div className="orders__scroll">
        <table className="table">
          <thead>
            <tr>
              <th scope="col">Product</th>
              <th scope="col">Colour</th>
              <th scope="col" className="num">
                Quantity
              </th>
              <th scope="col" className="num">
                Unit price
              </th>
              <th scope="col" className="num">
                Line total
              </th>
            </tr>
          </thead>
          <tbody>
            {order.lines.map((line) => (
              <tr key={`${line.product_id}-${line.variant_id}-${line.position}`}>
                <td>
                  <span className="orders__lineName">{line.product_name}</span>
                </td>
                <td>{line.variant_name}</td>
                <td className="num">{line.quantity}</td>
                <td className="num">
                  {formatMoney(line.unit_price_minor, line.currency)}
                  {/* Only where the product really was discounted at order
                      time. The backend sends this field only in that case, so
                      an absent one is not a missing price. */}
                  {line.original_unit_price_minor !== null ? (
                    <span className="orders__wasPrice">
                      was{" "}
                      {formatMoney(
                        line.original_unit_price_minor,
                        line.currency,
                      )}
                    </span>
                  ) : null}
                </td>
                <td className="num">
                  {formatMoney(line.line_total_minor, line.currency)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <dl className="orders__totals">
        <div className="orders__totalRow">
          <dt>Subtotal</dt>
          <dd className="num">
            {formatMoney(order.subtotal_minor, order.currency)}
          </dd>
        </div>
        {discounted ? (
          <div className="orders__totalRow">
            <dt>Customer saved</dt>
            <dd className="num orders__savings">
              {formatMoney(order.savings_minor, order.currency)}
            </dd>
          </div>
        ) : null}
        <div className="orders__totalRow orders__totalRow--final">
          <dt>Total charged</dt>
          <dd className="num" data-testid="order-total">
            {formatMoney(order.total_minor, order.currency)}
          </dd>
        </div>
      </dl>

      {/* Stated rather than left to be inferred from an absent row: an operator
          checking a total against a receipt should not have to wonder whether a
          charge is missing from this screen. */}
      <p className="orders__totalsNote">
        The total is what the products cost. OptiQ adds no tax and no delivery
        charge.
      </p>
    </Panel>
  );
}
